import { useState } from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import useTranslation from '../../hooks/useTranslation'
import usePath from '../../hooks/usePath'
import DisplayElement from '../display/DisplayElement'
import { TableDecorator } from '../table/Table'
import GridListCell from './GridListCell'
import LineActions from '../listView/LineActions'
import Connect from '../select/Connect'
import { Icon_Search } from '../../styles/icons'

const CardWrapper = styled.div`
    display: flex;
    flex-direction: column;
    border: 1px solid ${({ theme }) => theme.table.border};
    border-radius: 8px;
    overflow: hidden;
    background: ${({ theme }) => theme.table.background};
    color: ${({ theme }) => theme.font.color};
    position: relative;
    margin-bottom: 8px;
`

const CardHead = styled.div`
    font-size: var(--font-S);
    font-weight: 700;
    padding: 8px 16px;
    background: ${({ theme }) => theme.table.header};
    border-bottom: 1px solid ${({ theme }) => theme.table.border};
    cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
    color: ${({ $clickable, theme }) => $clickable ? theme.font.highlight : theme.font.color};
    text-decoration: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
`

const CardBody = styled.div`
    padding: 0;
`

const CardRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 8px;
    padding: 6px 16px;
    border-top: ${({ $first }) => $first ? 'none' : `1px solid ${({ theme }) => theme.table.borderTop}`};
`

const CardLabel = styled.p`
    margin: 0;
    font-size: var(--font-XS);
    color: ${({ theme }) => theme.font.label};
    font-weight: 600;
    flex-shrink: 0;
`

const CardValue = styled.div`
    font-size: var(--font-XS);
    color: ${({ theme }) => theme.font.color};
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex: 1;
    max-width: 60%;
    margin-left: 1rem;

    p {
        margin: 0;
    }
`

const MobileActions = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`

const HeadEditButton = styled.button`
    background: none;
    border: none;
    padding: 2px 4px;
    cursor: pointer;
    color: ${({ theme }) => theme.font.highlight};
    display: flex;
    align-items: center;
    opacity: 0.7;
    &:hover { opacity: 1; }
`

const ConnectOverlay = styled.div`
    padding: 8px 16px;
    border-top: 1px solid ${({ theme }) => theme.table.border};
    background: ${({ theme }) => theme.form.textControl.background};
`

const StatusDot = styled.div`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $status, theme }) => theme.emotive[$status].color};
    flex-shrink: 0;
`

const GridListCard = ({ row, rowIdx, mobileConfig, config, onAction, onUpdateCell, useStore, entity, isDirty, isSaved }) => {
    const { t } = useTranslation()
    const path = config.path
    const { url } = usePath(path || '')
    const isClickable = !!path
    const tableKey = config.tableKey || 'id'
    const [isEditingHead, setIsEditingHead] = useState(false)

    if (!mobileConfig) return null

    const headColumnId = mobileConfig?.left?.tag?.field ?? mobileConfig.headColumn
    const columns = mobileConfig.columns || []

    const headConnectCol = headColumnId
        ? (config.columns || []).find(col =>
            col.editable && col.type === 'connect' &&
            (col.id === headColumnId || col.displayField === headColumnId)
          )
        : null

    const handleAction = (menuItem, item) => {
        if (onAction) onAction(menuItem, item, rowIdx)
    }

    const handleConnectSetValue = (events) => {
        if (!Array.isArray(events)) return
        const updates = {}
        events.forEach(({ target: { name, value } }) => {
            if (value !== null && value !== undefined) updates[name] = value
        })
        onUpdateCell?.(rowIdx, updates)
        setIsEditingHead(false)
    }

    return (
        <CardWrapper>
            {headColumnId && (
                <CardHead
                    as={isClickable ? Link : 'div'}
                    to={isClickable ? `${url}${row[tableKey]}` : undefined}
                    $clickable={isClickable}
                >
                    <span>{row[headColumnId] ?? ''}</span>
                    <MobileActions>
                        {isDirty && !isSaved && <StatusDot $status="warning" />}
                        {isSaved && <StatusDot $status="success" />}
                        {headConnectCol && (
                            <HeadEditButton type="button" onClick={() => setIsEditingHead(v => !v)}>
                                {Icon_Search}
                            </HeadEditButton>
                        )}
                        {(config.lineActions || config.path) && (
                            <LineActions
                                lineActions={config.resolvedLineActions || config.lineActions}
                                item={row}
                                onAction={handleAction}
                            />
                        )}
                    </MobileActions>
                </CardHead>
            )}
            {isEditingHead && headConnectCol && (
                <ConnectOverlay>
                    <Connect
                        entity={headConnectCol.entity || entity}
                        mainValueId={row[headConnectCol.id]}
                        valueName={headConnectCol.displayField ? row[headConnectCol.displayField] : row[headConnectCol.id]}
                        valueMap={headConnectCol.valueMap}
                        menuItemField={headConnectCol.menuItemField || 'name'}
                        modalTitle={headConnectCol.modalTitle}
                        options={headConnectCol.options || {}}
                        setValue={handleConnectSetValue}
                        $width="100%"
                        $marginBottom={0}
                        autoOpen={true}
                    />
                </ConnectOverlay>
            )}
            <CardBody>
                {columns.map((col, idx) => {
                    if (col.id === headColumnId) return null
                    const isEditable = col.editable || false

                    return (
                        <CardRow key={col.id ?? idx} $first={false}>
                            <CardLabel>{t(col.label ?? col.name)}</CardLabel>
                            <CardValue>
                                {isEditable ? (
                                    <GridListCell
                                        column={col}
                                        row={row}
                                        rowIdx={rowIdx}
                                        editable={true}
                                        onUpdate={onUpdateCell}
                                        useStore={useStore}
                                        entity={entity}
                                    />
                                ) : (
                                    <p>
                                        <DisplayElement
                                            string={row[col.id]}
                                            type={col.type === 'amount' ? 'amount' : col.type}
                                            item={row}
                                            col={col}
                                        />
                                        {col.decorator && row[col.decorator] && (
                                            <TableDecorator>{row[col.decorator]}</TableDecorator>
                                        )}
                                    </p>
                                )}
                            </CardValue>
                        </CardRow>
                    )
                })}
            </CardBody>
        </CardWrapper>
    )
}

export default GridListCard
